/**
 * Create dialog for a cache rule. Pick a scope (file extension / directory /
 * path pattern) and a path, then choose the cache behavior: "Cached" sets a TTL
 * (with an optional honor-origin override) while "No-cache" bypasses caching
 * (ttl=0). The backend/SDK only support creating rules, so there is no edit mode.
 */
import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
  Button,
  Input,
  Switch,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@vnetwork/ui/primitives';
import { useToast } from '@vnetwork/ui/feedback';
import {
  useCreateWafCacheRule,
  useUpdateWafCacheRule,
  wafExtractError,
  type WafCacheOption,
  type WafCacheRule,
  type WafCacheRuleInput,
} from '@/api/waf';
import { trackFeatureAction } from '@/lib/analytics-events';
import styles from './CacheRuleFormDialog.module.css';

const DEFAULT_TTL = 3600;

/** Scope tokens that can be created (the `default` rule is not creatable). */
const FALLBACK_SCOPES: WafCacheOption[] = [
  { value: 'extension', label: 'File extension' },
  { value: 'directory', label: 'Directory' },
  { value: 'pattern', label: 'Path pattern' },
];

/** A stacked label + control form cell. */
function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
      <span style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>{label}</span>
      {children}
      {hint ? (
        <span style={{ color: 'var(--vn-fg-subtle)', fontSize: 'var(--vn-font-size-sm)' }}>{hint}</span>
      ) : null}
    </div>
  );
}

const PATH_PLACEHOLDER: Record<string, string> = {
  extension: '.jpg',
  directory: '/assets/',
  pattern: '/images/*',
};

export function CacheRuleFormDialog({
  serviceId,
  domain,
  scopes,
  rule,
  defaultCached = true,
  open,
  onOpenChange,
}: {
  serviceId: string;
  domain: string;
  /** Creatable scopes from the options endpoint (falls back to the canonical 3). */
  scopes?: WafCacheOption[];
  /** When set, the dialog edits this rule (PUT) instead of creating a new one. */
  rule?: WafCacheRule;
  /** Initial cache behavior on create — false opens on "No-cache" (the bypass tab). */
  defaultCached?: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { show } = useToast();
  const isEdit = !!rule;
  const scopeOpts = (scopes && scopes.length ? scopes : FALLBACK_SCOPES).filter(
    (s) => s.value !== 'default',
  );

  const initialScope = () => rule?.scope ?? scopeOpts[0]?.value ?? 'extension';
  const initialCached = () => (rule ? rule.ttl > 0 : defaultCached);

  const [scope, setScope] = useState(initialScope);
  const [path, setPath] = useState(rule?.paths?.[0] ?? '');
  const [cached, setCached] = useState(initialCached);
  const [ttl, setTtl] = useState(String(rule && rule.ttl > 0 ? rule.ttl : DEFAULT_TTL));
  const [honorOrigin, setHonorOrigin] = useState(rule?.honor_origin ?? false);

  // Reset the form whenever the dialog (re)opens (prefilled from `rule` on edit).
  useEffect(() => {
    if (open) {
      setScope(initialScope());
      setPath(rule?.paths?.[0] ?? '');
      setCached(initialCached());
      setTtl(String(rule && rule.ttl > 0 ? rule.ttl : DEFAULT_TTL));
      setHonorOrigin(rule?.honor_origin ?? false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const create = useCreateWafCacheRule(serviceId, {
    onSuccess: () => { show({ tone: 'success', title: 'Cache rule created' }); trackFeatureAction('waf', 'cache_rule', 'create'); onOpenChange(false); },
    onError: (e) => show({ tone: 'danger', title: wafExtractError(e, 'Create failed') }),
  });
  const update = useUpdateWafCacheRule(serviceId, {
    onSuccess: () => { show({ tone: 'success', title: 'Cache rule updated' }); trackFeatureAction('waf', 'cache_rule', 'edit'); onOpenChange(false); },
    onError: (e) => show({ tone: 'danger', title: wafExtractError(e, 'Update failed') }),
  });

  const ttlNum = Number(ttl);
  const valid =
    !!path.trim() && (!cached || (Number.isFinite(ttlNum) && ttlNum > 0));
  const pending = create.isPending || update.isPending;

  const submit = () => {
    if (!valid) return;
    const input: WafCacheRuleInput = {
      scope,
      path: path.trim(),
      ttl: cached ? ttlNum : 0,
      honor_origin: cached ? honorOrigin : false,
    };
    if (isEdit && rule) update.mutate({ domain, id: rule.id, input });
    else create.mutate({ domain, input });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="md">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit cache rule' : 'New cache rule'}</DialogTitle>
          <DialogDescription>
            {isEdit ? 'Update the' : 'Add a'} cache rule for <strong>{domain}</strong>. Choose "No-cache" to bypass caching for matching requests.
          </DialogDescription>
        </DialogHeader>
        <DialogBody style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
          <Field label="Scope">
            <Select value={scope} onValueChange={setScope} disabled={pending}>
              <SelectTrigger aria-label="Scope">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {scopeOpts.map((s) => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <Field
            label="Path"
            hint={
              scope === 'extension'
                ? 'A file extension, e.g. .jpg'
                : scope === 'directory'
                  ? 'A directory, e.g. /assets/'
                  : 'A path pattern, e.g. /images/*'
            }
          >
            <Input
              placeholder={PATH_PLACEHOLDER[scope] ?? '/path'}
              value={path}
              onChange={(e) => setPath(e.target.value)}
              disabled={pending}
            />
          </Field>

          <Field label="Cache behavior">
            <Select value={cached ? 'cached' : 'bypass'} onValueChange={(v) => setCached(v === 'cached')} disabled={pending}>
              <SelectTrigger aria-label="Cache behavior">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="cached">Cached (set TTL)</SelectItem>
                <SelectItem value="bypass">No-cache (bypass)</SelectItem>
              </SelectContent>
            </Select>
          </Field>

          {cached && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--vn-space-3)', alignItems: 'start' }}>
              <Field label="TTL (seconds)">
                <Input
                  type="number"
                  min={1}
                  className={styles.noSpin}
                  placeholder="3600"
                  value={ttl}
                  onChange={(e) => setTtl(e.target.value)}
                  disabled={pending}
                />
              </Field>
              <Field label="Honor origin" hint="Respect the origin's Cache-Control header.">
                <div style={{ display: 'inline-flex' }}>
                  <Switch checked={honorOrigin} onCheckedChange={setHonorOrigin} disabled={pending} />
                </div>
              </Field>
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <div style={{ flex: 1 }} />
          <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={pending}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={pending || !valid}>
            {isEdit ? 'Save changes' : 'Create rule'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default CacheRuleFormDialog;
